import React, { useEffect, useId, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Info } from 'lucide-react';
import Modal from '../../components/Modal/Modal';
import { createReminderCampaign } from '../../api/reminders';
import { useI18n } from '../../i18n/I18nContext';
import { errorText } from '../../utils/errorText';
import { formatDateTime } from '../../utils/datetime';
import ChannelPicker from '../Reminders/composer/ChannelPicker';
import LinkFields from '../Reminders/composer/LinkFields';
import MessageField from '../Reminders/composer/MessageField';
import useComposerOptions from '../Reminders/composer/useComposerOptions';
import {
  DEFAULT_CHANNELS, MESSAGE_MAX, TASHKENT, TITLE_MAX, insideSendWindow, normalizeChannels, tashkentHour,
} from '../Reminders/reminderUi';

/**
 * "Eslatma yuborish" on the student page: a message to this one student (audience SELECTED with
 * their id), through the channels the business has, optionally with a course link and a promo code.
 * It is sent at once; outside 09:00-21:00 Tashkent time the server holds it until 09:00 and the
 * form says so. The history card of the page picks the delivery up through `onSent`.
 * Mounted only while open.
 *
 *   {open && <SendReminderModal student={{ id, name }} onClose={close} onSent={reloadHistory} />}
 */
export default function SendReminderModal({ student, onClose, onSent }) {
  const { t } = useI18n();
  const uid = useId();
  const options = useComposerOptions();
  const [message, setMessage] = useState('');
  const [linkCourse, setLinkCourse] = useState('');
  const [promoId, setPromoId] = useState('');
  const [picked, setPicked] = useState(null); // null: the defaults, whichever channels are configured
  const [smsFallback, setSmsFallback] = useState(true);
  const [messageError, setMessageError] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const alive = useRef(true);
  // React's StrictMode runs this effect, its cleanup and the effect again in development: the flag
  // must come back to true, or every later `if (!alive.current)` would swallow the result of a save.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const channels = normalizeChannels(picked || DEFAULT_CHANNELS, options.channels);
  const smsSelected = channels.includes('SMS');
  const hour = tashkentHour();
  const held = hour !== null && !insideSendWindow(hour);
  const dirty = message.trim() !== '' || picked !== null || linkCourse !== '' || promoId !== '';

  const submit = async () => {
    if (!message.trim()) {
      setMessageError(t('reminders.compose.messageRequired'));
      return;
    }
    setMessageError('');
    setError('');
    setSending(true);
    try {
      const campaign = await createReminderCampaign({
        audience: 'SELECTED',
        userIds: [student.id],
        title: t('reminders.quick.autoTitle', { name: student.name || '' }).slice(0, TITLE_MAX),
        message: message.trim(),
        linkCourseId: linkCourse || undefined,
        discountId: promoId || undefined,
        channels,
        smsFallbackOnly: smsSelected ? smsFallback : undefined,
      });
      if (!alive.current) return;
      const goesLater = campaign?.scheduledAt && new Date(campaign.scheduledAt).getTime() > Date.now() + 60 * 1000;
      toast.success(goesLater
        ? t('reminders.compose.scheduledToast', { when: formatDateTime(campaign.scheduledAt, { timeZone: TASHKENT }) })
        : t('reminders.compose.sentToast'));
      onSent?.(campaign);
      onClose();
    } catch (err) {
      if (alive.current) setError(errorText(err, t('reminders.compose.sendError')));
    } finally {
      if (alive.current) setSending(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('reminders.quick.title')}
      size="lg"
      closeOnBackdrop={!dirty}
      as="form"
      onSubmit={submit}
      formProps={{ noValidate: true }}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('reminders.quick.cancel')}</button>
          <button type="submit" className="ds-btn ds-btn-primary" disabled={sending}>{t('reminders.quick.send')}</button>
        </>
      )}
    >
      <div className="ui-stack">
        <p className="sd-rem-for">{t('reminders.quick.forPerson', { name: student.name || '—' })}</p>

        <MessageField
          id={`${uid}-message`}
          label={t('reminders.message.textLabel')}
          value={message}
          onChange={setMessage}
          error={messageError}
          maxLength={MESSAGE_MAX}
          smsSelected={smsSelected}
          rows={4}
        />

        <LinkFields
          courses={options.courses}
          promos={options.promos}
          courseId={linkCourse}
          onCourseChange={setLinkCourse}
          promoId={promoId}
          onPromoChange={setPromoId}
        />

        <ChannelPicker
          value={channels}
          onChange={setPicked}
          channels={options.channels}
          smsFallbackOnly={smsFallback}
          onSmsFallbackChange={setSmsFallback}
        />

        {held && (
          <div className="ui-alert" data-tone="info">
            <Info aria-hidden="true" />
            <p>{t('reminders.compose.held')}</p>
          </div>
        )}
        {error && <p className="ui-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}
